import React from 'react';
import { CompleteKundali } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';

export const Grantha6_LalKitab: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails } = kundali;

  const farmanGroups = [
    {
      page: 35,
      pageHi: '३५',
      title: isHi ? 'सूर्य, चन्द्र एवं मंगल — लाल किताब फरमान व सावधानियां' : 'Sun, Moon & Mars — Lal Kitab Farmans & Safeguards',
      items: [
        {
          name: 'Sun (Surya)',
          farman: 'Honor father, arise before dawn, avoid sweets gifted by opponents in darkness; venerate elders and sacred sanctuaries.',
          caution: 'Keep pure drinking water in brass or copper vessels; avoid false promises during solar eclipse hours.'
        },
        {
          name: 'Moon (Chandra)',
          farman: 'Honor mother, never dispose of ancestral silver or liquid dairy assets in times of acute distress; touch elder women\'s feet for blessings.',
          caution: 'Drink water in a solid silver cup; never allow domestic wells or water storage to stagnate.'
        },
        {
          name: 'Mars (Mangal)',
          farman: 'Control sudden temper, refrain from harsh words during family meals; distribute sweet honey flatbread on Tuesdays.',
          caution: 'Bury pure wild honey in an unbaked earthen pot in desolate barren soil if afflicted.'
        }
      ]
    },
    {
      page: 36,
      pageHi: '३६',
      title: isHi ? 'बुध, गुरु एवं शुक्र — लाल किताब फरमान व सावधानियां' : 'Mercury, Jupiter & Venus — Lal Kitab Farmans & Safeguards',
      items: [
        {
          name: 'Mercury (Budha)',
          farman: 'Cleanse electronics, avoid storing broken musical instruments, empty sea shells or cracked glass; wear small silver wire.',
          caution: 'Feed green grass and soaked whole green moong to sacred cows every Wednesday morning.'
        },
        {
          name: 'Jupiter (Guru)',
          farman: 'Apply pure saffron (kesar) or turmeric tilak daily; venerate sacred preceptors, family priests, and traditional scriptures.',
          caution: 'Offer pure water to sacred Peepal tree roots without direct touching; never insult scholars.'
        },
        {
          name: 'Venus (Shukra)',
          farman: 'Maintain fragrant personal grooming and pristine hygiene; treat spouse as divine counterpart; avoid wearing torn clothes.',
          caution: 'Donate pure white cow curd, camphor, and jowar grain in temple; respect maiden daughters.'
        }
      ]
    },
    {
      page: 37,
      pageHi: '३७',
      title: isHi ? 'शनि, राहु एवं केतु — लाल किताब फरमान व सावधानियां' : 'Saturn, Rahu & Ketu — Lal Kitab Farmans & Safeguards',
      items: [
        {
          name: 'Saturn (Shani)',
          farman: 'Feed black stray dogs, crows, and river fish without fail; never consume alcohol or non-vegetarian food in native\'s residence.',
          caution: 'Perform Chhaya Daan by donating mustard oil after gazing at reflection in an iron bowl.'
        },
        {
          name: 'Rahu',
          farman: 'Keep head covered in sacred sanctums; ensure kitchen and bathroom drainage conduits flow clean without backlogs.',
          caution: 'Store a solid pure silver square block inside purse or pocket; immerse dry coconuts in flowing river.'
        },
        {
          name: 'Ketu',
          farman: 'Feed dual-colored (black-and-white) stray dog; care for maternal lineage, protect knees and joints with warm oils.',
          caution: 'Donate dual-colored warm woolen blanket in sanctuary during winter solstice or solar transition.'
        }
      ]
    }
  ];

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 43: AUDIT OF 7 ANCESTRAL KARMIC DEBTS (HIGH-DENSITY FULL PAGE)        */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ६ • लाल किताब पूर्वजन्म ऋण चक्र' : 'Grantha 6 • Ancestral Karmic Debts'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३४' : 'Page 34'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'लाल किताब सप्त पूर्वजन्म ऋण परीक्षण एवं प्रायश्चित्त विधान' : 'LAL KITAB 7 ANCESTRAL KARMIC DEBTS COMPLETE AUDIT'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'पूर्वजों अथवा स्वयं द्वारा विगत जन्मों में विस्मृत कर्तव्यों से उत्पन्न ७ ऋणों का सूक्ष्म निदान एवं कुल-प्रायश्चित्त।'
                : 'Canonical diagnostic audit of 7 ancestral debts and collective bloodline neutralization rituals.'}
            </p>
          </div>

          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-[11px] border-collapse">
              <thead>
                <tr className={`${tableHeaderBg} font-bold text-xs`}>
                  <th className="p-2">Ancestral Debt</th>
                  <th className="p-2">Causal Violation</th>
                  <th className="p-2">Astrological Diagnostic</th>
                  <th className="p-2">Neutralizing Farman</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { name: 'Pitra Rin (पितृ ऋण)', cause: 'Neglect/disrespect to father/elders', ind: 'Afflicted Sun or 9th house damage', rem: 'Collect equal silver/cash from all blood kin and donate to sacred temple' },
                  { name: 'Matri Rin (मातृ ऋण)', cause: 'Distress to mother; pollution of rivers', ind: 'Afflicted Moon or 4th house damage', rem: 'Collect silver from all kin and immerse in sacred flowing river' },
                  { name: 'Stri Rin (स्त्री ऋण)', cause: 'Deceit or harassment towards women', ind: 'Afflicted Venus or 2nd/7th house affliction', rem: 'Feed 100 cows fresh green fodder at cow sanctuary' },
                  { name: 'Swa Rin (स्व ऋण)', cause: 'Atheism or self-inflicted spiritual harm', ind: 'Afflicted 5th house or Jupiter', rem: 'Perform Surya Yajna and feed community sweet jaggery' },
                  { name: 'Nirdayi Rin (निर्दयी ऋण)', cause: 'Cruelty towards animals or subordinates', ind: 'Afflicted Saturn or Mars in 10th/11th', rem: 'Feed 100 fish daily or care for ailing manual laborers' },
                  { name: 'Ishwariya Rin (ईश्वरीय ऋण)', cause: 'Betrayal of vows made before sacred deities', ind: 'Afflicted 9th/12th house or Ketu', rem: 'Feed yellow roasted chickpeas to birds and visiting sadhus' },
                  { name: 'Kanya Rin (कन्या ऋण)', cause: 'Disregard for daughters, sisters or nieces', ind: 'Afflicted Mercury or 3rd/6th node', rem: 'Gift yellow attire and sweets to maiden daughters in festival' }
                ].map((debt, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-2 font-bold text-astro-primary">{debt.name}</td>
                    <td className="p-2 text-gray-700">{debt.cause}</td>
                    <td className="p-2 font-mono text-[10px] text-amber-900 bg-amber-50/50">{debt.ind}</td>
                    <td className="p-2 text-gray-800 italic">{debt.rem}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={`rounded-xl border p-3 text-xs space-y-1 ${innerCardBg}`}>
            <span className={`font-bold font-serif uppercase tracking-wider block ${headingPrimary}`}>
              {isHi ? 'सामूहिक प्रायश्चित्त सिद्धान्त:' : 'Collective Kinship Resolution Rule:'}
            </span>
            <p className="text-[11px] text-gray-700 leading-relaxed">
              Lal Kitab strictly requires that when an ancestral debt is diagnosed, equal contribution must be collected from all blood-related family members (brothers, uncles, children) before offering the remedy, ensuring shared lineage clearance.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ६ • पृष्ठ ३४' : 'Grantha 6 • Page 34')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 44 TO 46: 9-PLANET FARMANS (3 PLANETS PER PAGE, HIGH DENSITY)       */}
      {/* ========================================================================= */}
      {farmanGroups.map((group, gIdx) => (
        <div key={gIdx} className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
          <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{group.title}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${group.pageHi}` : `Page ${group.page}`}</span>
          </div>

          <div className="space-y-3 my-auto">
            <div className="text-center space-y-0.5 border-b pb-1.5">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {group.title}
              </h2>
            </div>

            <div className="space-y-3">
              {group.items.map((item, iIdx) => (
                <div key={iIdx} className={`rounded-xl border p-3.5 space-y-1.5 ${innerCardBg}`}>
                  <div className="flex items-center justify-between border-b pb-1">
                    <span className="font-bold text-sm text-astro-primary font-serif">{item.name}</span>
                    <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      Lal Kitab Farman
                    </span>
                  </div>
                  <p className="text-xs font-serif font-bold text-gray-800 leading-relaxed">
                    {item.farman}
                  </p>
                  <div className="text-[11px] text-gray-700 border-t pt-1 flex items-start gap-1">
                    <span className="font-bold text-astro-primary shrink-0">{isHi ? 'सावधानी व वर्जना:' : 'Precaution & Safeguard:'}</span>
                    <span className="italic">{item.caution}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {renderPageFooter(isHi ? `ग्रन्थ ६ • पृष्ठ ${group.pageHi}` : `Grantha 6 • Page ${group.page}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 38: THE CANONICAL 43-DAY PROTOCOL & REMEDIAL CATALOG (100% FILLED)    */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ६ • ४३ दिवसीय अखण्ड उपाय विधान' : 'Grantha 6 • 43-Day Totke Protocol'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३८' : 'Page 38'}</span>
        </div>

        <div className="space-y-3 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? '४३ दिवसीय अखण्ड उपाय नियम एवं शास्त्रीय सामग्री तालिका' : 'THE CANONICAL 43-DAY REMEDIAL PROTOCOL & MATERIALS'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? 'उपायों का अटूट क्रम, दिन के समय निष्पादन, जल प्रवाह व भू-विसर्जन के अचूक नियम।'
                : 'Daylight execution, strict 43-day continuity, and material concordance rules.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className={`rounded-xl border p-3 space-y-1.5 ${tableRowEven}`}>
              <span className="font-bold text-astro-primary block border-b pb-1">1. The 43-Day Unbroken Rule (अखण्ड नियम)</span>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                Remedies must be performed continuously for 43 consecutive days without a single missing day. If interrupted, the count restarts at Day 1.
              </p>
            </div>
            <div className={`rounded-xl border p-3 space-y-1.5 ${tableRowEven}`}>
              <span className="font-bold text-astro-primary block border-b pb-1">2. Daylight Execution (दिन का समय)</span>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                All Lal Kitab totkes must be performed between sunrise and sunset under natural daylight. Night remedies are forbidden unless explicitly stipulated.
              </p>
            </div>
            <div className={`rounded-xl border p-3 space-y-1.5 ${tableRowEven}`}>
              <span className="font-bold text-astro-primary block border-b pb-1">3. Running Water Immersion (जल प्रवाह)</span>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                Objects designated for water disposal must be released into clean, continuously flowing natural rivers, facing in the direction of the stream flow.
              </p>
            </div>
            <div className={`rounded-xl border p-3 space-y-1.5 ${tableRowEven}`}>
              <span className="font-bold text-astro-primary block border-b pb-1">4. Earth Burial Rules (भू-विसर्जन)</span>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                Burials must be performed in uncultivated, desolate soil where human feet do not routinely tread, maintaining reverence.
              </p>
            </div>
          </div>

          {/* Planetary Materials Matching Table */}
          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-[11px] border-collapse">
              <thead>
                <tr className={`${tableHeaderBg} font-bold text-xs`}>
                  <th className="p-1.5">Planet</th>
                  <th className="p-1.5">Primary Metal</th>
                  <th className="p-1.5">Botanical / Grain Substance</th>
                  <th className="p-1.5">Sanctuary Offering</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { p: 'Sun', m: 'Pure Copper', g: 'Wheat & Jaggery', s: 'Red lotus / saffron' },
                  { p: 'Moon', m: 'Solid Silver', g: 'White Rice & Milk', s: 'Cow milk & curd' },
                  { p: 'Mars', m: 'Copper / Red Coral', g: 'Red Masoor Dal', s: 'Sweet honey flatbread' },
                  { p: 'Mercury', m: 'Bronze / Brass', g: 'Whole Green Moong', s: 'Green cardamom / grass' },
                  { p: 'Jupiter', m: 'Pure Gold', g: 'Yellow Chana Dal', s: 'Turmeric & yellow flowers' },
                  { p: 'Venus', m: 'Silver / Platinum', g: 'White Curd & Jowar', s: 'Camphor & scented oil' },
                  { p: 'Saturn', m: 'Iron / Lead', g: 'Black Urad & Mustard Oil', s: 'Iron nail / black sesame' }
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                    <td className="p-1.5 font-bold text-astro-primary">{row.p}</td>
                    <td className="p-1.5 font-mono">{row.m}</td>
                    <td className="p-1.5">{row.g}</td>
                    <td className="p-1.5 italic text-gray-700">{row.s}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ६ • पृष्ठ ३८' : 'Grantha 6 • Page 38')}
      </div>
    </>
  );
};
